// ═══════════════════════════════════════════════════════════════════════════
// Rentabilidad — Función pura compartida frontend ↔ backend
// ───────────────────────────────────────────────────────────────────────────
// Calcula la rentabilidad de una línea de orden (item dentro de una venta)
// a partir de inputs ya normalizados/prorrateados a nivel línea.
//
// Por qué pura:
//   - Garantiza que el cálculo del dashboard (FE) y el cálculo persistido
//     (BE callable) den exactamente el mismo resultado.
//   - No depende de Firestore, ni de React, ni del usuario logueado.
//   - Determinística: mismos inputs → mismo output.
//
// La función NO normaliza ni prorratea. Quien la llama tiene que prepararle
// los inputs a nivel línea (ej: comisión total de la orden × prorrateo).
//
// Mirror backend: functions/lib/rentabilidad.js (mismo código, sintaxis CJS).
// ═══════════════════════════════════════════════════════════════════════════

// ── FORMULA_VERSION ────────────────────────────────────────────────────────
// 1.0 → 1.1 (11-ago-2026, ítem 17 / ADR 078): el envío del seller pasa a
// repartirse SOLO entre los ítems que se lo cobran al vendedor. La cascada
// incremental saltea las órdenes cuya `formulaVersion` coincide con la
// vigente (`functions/rentabilidad.js:519`), así que bumpear el número ES el
// recálculo: la próxima corrida incremental rehace todo lo que quedó con la
// fórmula vieja, sin script de migración ni pasada forzada a mano.
// 1.1 → 1.2 (15-sep-2026, ADR 2026-09-04 enmienda E6): la línea puede traer
// `ingresoEnvioLinea` — el envío que el COMPRADOR le paga al comerciante
// (Tienda Nube). Entra al ingreso bruto (IVA débito, IIBB, neto al banco,
// base del margen). Sin el insumo (ML, donde el envío del comprador es de ML)
// la aritmética es idéntica a 1.1.
// 1.2 → 1.3 (15-sep-2026, packs y combos): el `costoUnit` de la línea es la
// composición de sus vínculos (Σ cantidad × costo por componente). Misma
// aritmética; cambia el insumo — ver `functions/lib/costoComposicion.js`.
// 1.3 → 1.4 (15-sep-2026, IVA por componente): combo con mezcla de alícuotas
// ⇒ `partesIva` reparte mercadería y costo y cada parte lleva su IVA; los
// servicios llevan `ivaServiciosPct`. Línea simple: idéntica a 1.3.
// 1.4 → 1.5 (27-oct-2026, Sincro A A.6b-4 parte 3): el envío del seller de
// ML deja de caer en `payments[].shipping_cost` (lo que pagó el comprador):
// sin dato es 0 con origen `no-informada`, y 0 conocido sólo si la orden no
// tiene envío. La aritmética de la línea no cambia; cambia el insumo. TN no
// cambia ningún número (su envío sale de su adapter): sólo se re-sella.
export const FORMULA_VERSION = '1.5'

// Estados que puede tener la rentabilidad de una línea/orden:
//   - 'calculada'        → tieneLink && tieneCosto → todos los outputs son válidos
//   - 'pendiente_costo'  → tieneLink && !tieneCosto → falta cargar costoPromedio del producto
//   - 'sin_link'         → !tieneLink → la publicación no está vinculada al catálogo
//
// La AUSENCIA del campo `_rentabilidad` en el doc significa "no_calculada"
// (todavía no pasó por el botón de cálculo).
export const ESTADOS_RENTABILIDAD = Object.freeze({
  CALCULADA: 'calculada',
  PENDIENTE_COSTO: 'pendiente_costo',
  SIN_LINK: 'sin_link',
})

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// ── Orígenes de un costo persistido en la línea (ADR 2026-09-04 enmienda E8) ──
// Catálogo CERRADO. Cada costo de `porLinea` que puede venir de más de un lado
// (comisión, costo por transacción de TN, envío) lleva su origen en
// `origenes.<costo>`; el front deriva "no informado" de acá (una sola fuente,
// `derivarCostosNoInformados`). Un valor fuera de esta lista es un bug.
export const ORIGENES_COSTO = Object.freeze({
  TABLA_OFICIAL: 'tabla-oficial',       // reconstruido desde un catálogo curado
  API: 'api',                           // medido: lo informó la plataforma
  API_TRANSACCION_TN: 'api-transaccion-tn', // medido: la transacción del pago que Tienda Nube agrega a la orden (v9, aggregates=transactions)
  API_SIN_ETIQUETA: 'api-sin-etiqueta', // envío: la API da costo = lo cobrado (sin etiqueta real)
  EXENTA: 'exenta',                     // no aplica por regla oficial (p. ej. Pago Nube sin costo por transacción)
  NO_INFORMADA: 'no-informada',         // sin dato ni regla: entra 0 y la pantalla lo dice
})

// Costo de `origenes` → literal que las pantallas y los drills ya conocían
// (`costosNoInformados`, hasta 1.2 persistido; desde 1.2 DERIVADO al leer).
export const COSTOS_DECLARABLES = Object.freeze({
  comision: 'comision-pasarela',
  costoTransaccionTn: 'costo-transaccion-tn',
  envio: 'envio',
})

/**
 * PURA. Los costos que la línea NO informa, derivados de `origenes`: las
 * claves con origen `no-informada`, en el orden de COSTOS_DECLARABLES. Una
 * línea sin `origenes` (anterior a 1.2) devuelve su `costosNoInformados`
 * persistido, si lo trae; si no, [].
 */
export function derivarCostosNoInformados(persisted) {
  const origenes = persisted?.origenes
  if (!origenes || typeof origenes !== 'object') {
    return Array.isArray(persisted?.costosNoInformados) ? [...persisted.costosNoInformados] : []
  }
  const out = []
  for (const [costo, literal] of Object.entries(COSTOS_DECLARABLES)) {
    if (origenes[costo] === ORIGENES_COSTO.NO_INFORMADA) out.push(literal)
  }
  return out
}

/**
 * Calcula la rentabilidad neta de una línea de orden.
 *
 * @param {Object} input — todo a nivel línea, ya prorrateado si vino de orden.
 * @param {boolean} input.tieneLink           — la publicación está vinculada a un producto del catálogo
 * @param {boolean} input.tieneCosto          — el producto vinculado tiene costoPromedio cargado
 * @param {number}  input.subtotal            — unit_price × quantity (lo que paga el cliente, IVA incluido)
 * @param {number}  input.qty                 — cantidad
 * @param {number}  input.costoUnit           — costo unitario del producto (ARS, IVA incluido si discrimina)
 * @param {number}  input.comisionMLTotal     — total cobrado por ML como fees (sale_fee o marketplace_fee)
 * @param {number}  input.envioSellerLinea    — costo de envío que paga el seller (atribuido a la línea)
 * @param {number}  [input.ingresoEnvioLinea] — envío que el COMPRADOR le paga al comerciante (ingreso, atribuido a la línea; 0 si el canal lo cobra la plataforma, como ML)
 * @param {Object|null} input.billing         — desglose real desde billing/integration (opcional)
 * @param {number}  input.billing.sellingFee  — comisión variable real (a nivel línea)
 * @param {number}  input.billing.fixedFee    — fixed fee real
 * @param {number}  input.billing.financingCharge — cargo por cuotas real
 * @param {number}  input.adUnit              — costo de publicidad ML por unidad
 * @param {number}  input.ivaPct              — alícuota de IVA del producto (0.21, 0.105, 0)
 * @param {Object}  input.flags               — { discriminaIVA, pagaIIBB, pagaImpCredDebito, aplicaGananciasEstimado }
 * @param {number}  input.iibbRate            — tasa de IIBB (ej: 0.04)
 * @param {number}  input.impCredDebitoRate   — tasa de impuesto al débito/crédito (ej: 0.018)
 * @param {number}  input.factorGanancias     — factor de estimación de ganancias (ej: 0.08)
 *
 * @returns {Object} resultado con estado, valores finales y breakdown completo.
 */
/**
 * PURA. Normaliza `partesIva` a pesos que suman 1 (ingreso y costo por
 * separado). Sin partes, o con partes sin peso, una sola con `ivaPct`. Una
 * parte con `ivaPct` null toma la de la línea. Las partes con referencias
 * todas en 0 se reparten en partes iguales — nunca NaN.
 */
function normalizarPartesIva(partesIn, ivaPct) {
  const lista = Array.isArray(partesIn) ? partesIn.filter(Boolean) : []
  if (lista.length === 0) return [{ pesoIngreso: 1, pesoCosto: 1, ivaPct: num(ivaPct) }]
  const tI = lista.reduce((s, p) => s + Math.max(num(p.ingresoRef), 0), 0)
  const tC = lista.reduce((s, p) => s + Math.max(num(p.costoRef), 0), 0)
  return lista.map(p => ({
    pesoIngreso: tI > 0 ? Math.max(num(p.ingresoRef), 0) / tI : 1 / lista.length,
    pesoCosto: tC > 0 ? Math.max(num(p.costoRef), 0) / tC : 1 / lista.length,
    ivaPct: p.ivaPct != null ? num(p.ivaPct) : num(ivaPct),
  }))
}

export function calcularRentabilidadLinea(input) {
  const {
    tieneLink = false,
    tieneCosto = false,
    subtotal: subtotalIn,
    qty: qtyIn,
    costoUnit,
    comisionMLTotal,
    envioSellerLinea,
    ingresoEnvioLinea,
    billing,
    adUnit,
    ivaPct: ivaPctIn,
    ivaServiciosPct: ivaServiciosPctIn,
    partesIva: partesIvaIn,
    flags = {},
    iibbRate,
    impCredDebitoRate,
    factorGanancias,
  } = input || {}

  // ─── Estado ──────────────────────────────────────────────────────────────
  let estado
  if (!tieneLink) estado = ESTADOS_RENTABILIDAD.SIN_LINK
  else if (!tieneCosto) estado = ESTADOS_RENTABILIDAD.PENDIENTE_COSTO
  else estado = ESTADOS_RENTABILIDAD.CALCULADA

  // ─── Inputs base ─────────────────────────────────────────────────────────
  const subtotal = num(subtotalIn)
  const qty = num(qtyIn)
  const ivaPct = num(ivaPctIn)
  const ivaServiciosPct = ivaServiciosPctIn != null ? num(ivaServiciosPctIn) : ivaPct
  const comisionMLFinal = num(comisionMLTotal)
  const envioSeller = num(envioSellerLinea)
  // Envío que el COMPRADOR le pagó al comerciante (canales donde el envío es
  // ingreso del seller, p. ej. Tienda Nube). ML no lo pasa → 0 → 1.1 exacta.
  const ingresoEnvio = num(ingresoEnvioLinea)
  const ingresoBruto = subtotal + ingresoEnvio

  // ─── Costos directos ─────────────────────────────────────────────────────
  const costoProducto = num(costoUnit) * qty
  const publicidad = num(adUnit) * qty

  // ─── Desglose de comisión ML (real vs. agregado) ─────────────────────────
  // Si la orden está enriquecida con billing/integration, tenemos los 3
  // conceptos separados (selling, fixed, financing). Si no, solo tenemos un
  // total y dejamos las otras dos columnas en cero (no estimamos).
  const tieneBilling = !!billing
  let comisionPura, fixedFee, cargoCuotas
  if (tieneBilling) {
    comisionPura = num(billing.sellingFee)
    fixedFee = num(billing.fixedFee)
    cargoCuotas = num(billing.financingCharge)
  } else {
    comisionPura = comisionMLFinal
    fixedFee = 0
    cargoCuotas = 0
  }

  // ─── Cascada IVA (solo si discrimina) ────────────────────────────────────
  // ML Ads se factura SIN IVA → no hay crédito recuperable sobre publicidad.
  let ivaDebito = 0
  let ivaCredComision = 0
  let ivaCredEnvio = 0
  let ivaCredCompra = 0
  let ivaNeto = 0
  // 1.4 — IVA por componente (combos con mezcla de alícuotas, decisión de
  // Mariano 15-sep-2026: "se factura separado los ítems en la misma factura y
  // eso normaliza el IVA"). `partesIva` reparte la mercadería y su costo entre
  // componentes por PESO (ingresoRef: precio de lista × cantidad, o costo si
  // falta; costoRef: cantidad × costo) y cada parte lleva SU alícuota. Sin
  // `partesIva` (línea simple) hay una sola parte con `ivaPct`: aritmética
  // idéntica a 1.3. Los servicios (comisión, envío) llevan `ivaServiciosPct`
  // (default: `ivaPct`, la conducta de siempre).
  const partes = normalizarPartesIva(partesIvaIn, ivaPct)
  const ivaMixto = partes.length > 1 && partes.some(p => p.ivaPct !== partes[0].ivaPct)
  const rServ = ivaServiciosPct > 0 ? ivaServiciosPct / (1 + ivaServiciosPct) : 0
  if (flags.discriminaIVA && (ivaPct > 0 || ivaServiciosPct > 0)) {
    for (const p of partes) {
      const r = p.ivaPct > 0 ? p.ivaPct / (1 + p.ivaPct) : 0
      ivaDebito     += subtotal * p.pesoIngreso * r
      ivaCredCompra += costoProducto * p.pesoCosto * r
    }
    ivaDebito       += ingresoEnvio * rServ
    ivaCredComision  = comisionMLFinal * rServ
    ivaCredEnvio     = envioSeller * rServ
    ivaNeto          = ivaDebito - ivaCredComision - ivaCredEnvio - ivaCredCompra
  }

  // ─── IIBB ────────────────────────────────────────────────────────────────
  // Base neta de IVA, parte por parte (el envío cobrado con la alícuota de servicios).
  const baseIibb = partes.reduce((s, p) => s + (subtotal * p.pesoIngreso) / (1 + p.ivaPct), 0) + ingresoEnvio / (1 + ivaServiciosPct)
  const iibb = flags.pagaIIBB
    ? baseIibb * num(iibbRate)
    : 0

  // ─── Impuesto al débito/crédito (sobre lo que entra al banco) ────────────
  const netoQueEntraAlBanco = ingresoBruto - comisionMLFinal - envioSeller
  const impCredDebito = (flags.pagaImpCredDebito && netoQueEntraAlBanco > 0)
    ? netoQueEntraAlBanco * num(impCredDebitoRate)
    : 0

  // ─── Cascada hasta margen residual ───────────────────────────────────────
  const ingresoNetoPreIva = ingresoBruto - comisionMLFinal - envioSeller
  const mbPreIva = ingresoNetoPreIva - costoProducto
  const rentBrutaPostImp = mbPreIva - ivaNeto - iibb - impCredDebito
  const margenResidual = rentBrutaPostImp - publicidad

  // ─── Estimación de ganancias (con publicidad) ────────────────────────────
  const estGanancias = (flags.aplicaGananciasEstimado && margenResidual > 0)
    ? margenResidual * num(factorGanancias)
    : 0

  // ─── Renta neta final (con publicidad) ───────────────────────────────────
  const rentaNeta = margenResidual - estGanancias
  const margenPct = ingresoBruto > 0 ? rentaNeta / ingresoBruto : null

  // ─── Renta neta SIN publicidad ───────────────────────────────────────────
  // Sirve para comparar producto/canal sin contaminar con ads, y es el
  // número que vamos a usar para la BCG por margen sin publicidad.
  // Recalculamos las ganancias estimadas sin restar publicidad para no
  // arrastrar el sesgo del ad spend en la base de cálculo.
  const margenResidualSinPub = rentBrutaPostImp
  const estGananciasSinPub = (flags.aplicaGananciasEstimado && margenResidualSinPub > 0)
    ? margenResidualSinPub * num(factorGanancias)
    : 0
  const rentaNetaSinPub = margenResidualSinPub - estGananciasSinPub
  const margenSinPubPct = ingresoBruto > 0 ? rentaNetaSinPub / ingresoBruto : null

  // ─── Output ──────────────────────────────────────────────────────────────
  // Devuelve SIEMPRE los números calculados; el caller decide si los persiste
  // o no según el estado. Esto permite que el dashboard renderice igual que
  // antes (mostrando inflados cuando falta costo) sin que el callable los
  // persista como verdad.
  return {
    estado,
    rentaNeta,
    rentaNetaSinPub,
    margenPct,
    margenSinPubPct,
    breakdown: {
      subtotal,
      qty,
      // costos directos
      costoProducto,
      publicidad,
      // ML fees
      comisionMLFinal,
      comisionPura,
      fixedFee,
      cargoCuotas,
      tieneBilling,
      // envío
      envioSeller,
      ingresoEnvio,
      // IVA
      ivaPct,
      ivaServiciosPct,
      ivaMixto,
      ivaDebito,
      ivaCredComision,
      ivaCredEnvio,
      ivaCredCompra,
      ivaNeto,
      // otros impuestos
      iibb,
      impCredDebito,
      // cascada
      mbPreIva,
      rentBrutaPostImp,
      margenResidual,
      estGanancias,
      // sin publicidad
      margenResidualSinPub,
      estGananciasSinPub,
    },
    formulaVersion: FORMULA_VERSION,
  }
}

/**
 * Helper para extraer solo los campos que se persisten en Firestore.
 * El breakdown completo NO se persiste — vive solo en runtime para el
 * detalle/modal del dashboard. En el doc guardamos los datos que se usan
 * para los rollups (Capa 2 y 3) y para auditoría.
 *
 * @param {Object} resultado — output de calcularRentabilidadLinea
 * @returns {Object} doc apto para guardar en mlVentas.{lineaId}._rentabilidad
 */
export function extractPersistencia(resultado) {
  if (!resultado) return null
  const { estado, rentaNeta, rentaNetaSinPub, margenPct, margenSinPubPct, formulaVersion, breakdown } = resultado

  // Si no es 'calculada', persistimos solo el estado para que el dashboard
  // pueda diferenciar entre "no procesada" y "procesada con error".
  if (estado !== ESTADOS_RENTABILIDAD.CALCULADA) {
    return {
      estado,
      formulaVersion,
      // Tokens de auditoría — útiles aún sin valores finales
      qty: breakdown?.qty ?? 0,
      subtotal: breakdown?.subtotal ?? 0,
    }
  }

  return {
    estado,
    formulaVersion,
    rentaNeta,
    rentaNetaSinPub,
    margenPct,
    margenSinPubPct,
    qty: breakdown.qty,
    subtotal: breakdown.subtotal,
    // Inputs clave para auditoría / debugging (no es el breakdown completo)
    costoProducto: breakdown.costoProducto,
    publicidad: breakdown.publicidad,
    comisionMLFinal: breakdown.comisionMLFinal,
    envioSeller: breakdown.envioSeller,
    ingresoEnvio: breakdown.ingresoEnvio,
    ivaNeto: breakdown.ivaNeto,
    ivaMixto: breakdown.ivaMixto,
    iibb: breakdown.iibb,
    impCredDebito: breakdown.impCredDebito,
    estGanancias: breakdown.estGanancias,
  }
}
